import React, { useState, useEffect, useRef } from 'react';
import {
	ChevronLeft,
	Sparkles,
	Zap,
	Users,
	BarChart3,
	Target,
	Building2,
	Calendar,
	Hexagon,
	Mail,
	Link2,
	Phone,
	ExternalLink,
	Globe,
	Briefcase,
	Clock,
	CheckSquare,
	Plus,
	RefreshCw,
	Check,
	Loader2,
	AlertTriangle,
	TrendingDown,
	Scale,
	Landmark,
	CalendarClock
} from 'lucide-react';
import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import { useDialProps } from 'App/components/hooks/appHooks';
import { formatRevenue } from './CandidateRow';

type Dossier = {
	summary: string;
	descriptionLong?: string | null;
	financials?: {
		revenue?: string | null;
		employees?: string | null;
		growth?: string | null;
		profitMargin?: string | null;
	};
	products?: Array<{ name: string; description?: string }>;
	customers?: Array<{ name: string }>;
	people?: Array<{ name: string; title: string; email?: string; linkedin?: string }>;
	talkingPoints?: string[];
	generatedAt?: string;
};

type Candidate = {
	prospectingId: string;
	/** Absent for a company that has not been through a scoring run - the V2 search rows. */
	totalScore?: number;
	name?: string;
	domain?: string;
	countryCode?: string;
	employees?: number;
	revenue?: number;
	city?: string;
	naceCode?: string;
	founded?: string;
	contacts?: Array<{
		name: string;
		role: string;
		linkedin?: string;
		email?: string;
		phone?: string;
		contact?: {
			email?: string;
			phone?: string;
			linkedin?: string;
			emailSource?: string;
			phoneSource?: string;
			linkedinSource?: string;
		};
		source?: string;
		isPrimary?: boolean;
	}>;
	newsSignals?: Array<{
		type: string;
		title: string;
		summary?: string;
		date?: string;
		source?: string;
		sourceLabel?: string;
		confidence?: string;
	}>;
	inCRM?: boolean;
	crmClient?: { id: number; name: string; matchReason?: string };
};

/**
 * Pill label per signal type. Uses the platform-wide
 * `prospectingAgent.newsSignal.<type>` keys; falls back to the English label
 * when a translation is missing so a brand-new signal type still renders.
 */
const SIGNAL_FALLBACKS: Record<string, string> = {
	capital_raised: 'Funding',
	partnership: 'Partnership',
	expansion_into_new_market: 'Expansion',
	product_launch: 'Product',
	large_contract_won: 'Contract Won',
	layoffs: 'Layoffs',
	management_change: 'Leadership',
	acquired_by_other_company: 'Acquired',
	acquired_other_company: 'Acquisition',
	merged_with_other_company: 'Merger',
	divestiture: 'Divestiture',
	ipo: 'IPO',
	large_contract_lost: 'Contract Lost',
	exit_from_market: 'Market Exit',
	legal_challenge: 'Legal',
	financial_distress: 'Financial Distress',
	risk_of_bankruptcy: 'Bankruptcy Risk',
	in_reconstruction: 'Reconstruction',
	in_liquidation: 'Liquidation'
};

function signalLabel(type: string): string {
	const key = `prospectingAgent.newsSignal.${type}`;
	const translated = T(key);
	if (translated && translated !== key) return translated;
	return SIGNAL_FALLBACKS[type] || type;
}

/**
 * Display name for a data-connector source. Uses `prospectingAgent.source.<key>`
 * translations, falling back to the English label so a newly-added connector
 * (e.g. lusha/apollo/hunter) still renders before its key is translated.
 *
 * The line is drawn at who chose the supplier (WEB-10711):
 *
 * - **Suppliers behind our own product** — the company register, our web search
 *   — are not named. Which register we buy from is a commercial detail the rest
 *   of the product does not publish, and naming it was the only place in
 *   Upsales that did.
 * - **Connectors the customer configured themselves** — Lusha, Apollo, Hunter,
 *   installed with their own credentials in AI Workspace → Connections — keep
 *   their names. The customer set them up and pays the vendor, so seeing which
 *   one produced a channel is the transparency WEB-7251 added, not a leak.
 *
 * Note the second bullet is about *whose call produced the value*, and this
 * function only knows *which connector's name is on the row*. The contact cache
 * is shared across customers, so a Lusha badge here can still be a channel
 * another customer paid for — WEB-10121 carries the per-channel attribution
 * that closes that, and this map is what it will feed.
 *
 * The keys stay as they are — they are what the payloads and the logs carry.
 */
const SOURCE_FALLBACKS: Record<string, string> = {
	creditsafe: 'Company register',
	exa: 'Web',
	website: 'Website',
	linkedin: 'LinkedIn',
	crm: 'CRM',
	manual: 'Manual',
	lusha: 'Lusha',
	apollo: 'Apollo',
	hunter: 'Hunter'
};

function sourceLabel(source: string): string {
	const key = `prospectingAgent.source.${source}`;
	const translated = T(key);
	if (translated && translated !== key) return translated;
	return SOURCE_FALLBACKS[source] || source;
}

/**
 * Resolve the connector that supplied a single channel (phone/email/linkedin).
 * Enrichment connectors (lusha/apollo/hunter) tag the channel directly; a
 * channel that came from contact discovery — or the legacy `existing` marker —
 * falls back to the contact's discovery source.
 */
function channelSource(channelSrc: string | undefined, discoverySrc: string | undefined): string | undefined {
	if (!channelSrc || channelSrc === 'existing') return discoverySrc;
	return channelSrc;
}

const signalTone = (type: string): string => {
	switch (type) {
		case 'capital_raised':
		case 'large_contract_won':
		case 'ipo':
			return 'positive';
		case 'layoffs':
		case 'financial_distress':
		case 'in_reconstruction':
		case 'exit_from_market':
			return 'warn';
		case 'acquired_by_other_company':
		case 'risk_of_bankruptcy':
		case 'in_liquidation':
		case 'large_contract_lost':
		case 'legal_challenge':
			return 'risk';
		default:
			return 'info';
	}
};

const SignalIcon = ({ type }: { type: string }) => {
	const size = 16;
	switch (type) {
		case 'capital_raised':
			return <Zap style={{ width: size, height: size }} />;
		case 'partnership':
			return <Users style={{ width: size, height: size }} />;
		case 'expansion_into_new_market':
			return <Globe style={{ width: size, height: size }} />;
		case 'product_launch':
			return <Sparkles style={{ width: size, height: size }} />;
		case 'large_contract_won':
			return <Briefcase style={{ width: size, height: size }} />;
		case 'layoffs':
			return <Clock style={{ width: size, height: size }} />;
		case 'risk_of_bankruptcy':
		case 'in_liquidation':
			return <AlertTriangle style={{ width: size, height: size }} />;
		case 'financial_distress':
		case 'large_contract_lost':
			return <TrendingDown style={{ width: size, height: size }} />;
		case 'legal_challenge':
			return <Scale style={{ width: size, height: size }} />;
		case 'ipo':
			return <Landmark style={{ width: size, height: size }} />;
		case 'in_reconstruction':
			return <RefreshCw style={{ width: size, height: size }} />;
		case 'merged_with_other_company':
			return <Building2 style={{ width: size, height: size }} />;
		case 'divestiture':
		case 'exit_from_market':
			return <Globe style={{ width: size, height: size }} />;
		default:
			return <Zap style={{ width: size, height: size }} />;
	}
};

const avatarColors = ['#1d3d48', '#5F7F6A', '#3B6BA8', '#8B5A8F', '#B8935A', '#B84A3E'];

/** What POST /prospectingAgentRuns/sync answers for `mode: 'sync'`. */
export type SyncToCrmResult = {
	status: string;
	contactsCreated?: number;
	contactsExisting?: number;
	contactsEnriched?: number;
	fieldsUpdated?: string[];
};

const CompanyDossier = ({
	candidate,
	onBack,
	onImport,
	onSync,
	onEnrichStarted,
	onPlanStarted,
	refreshToken
}: {
	candidate: Candidate & Record<string, any>;
	onBack: () => void;
	/** `contactNames` is the rep's pick of the decision-makers; undefined adds everyone discovered. */
	onImport: (pids: string[], contactNames?: string[]) => void;
	onSync?: (
		pid: string,
		clientId: number,
		mode: 'sync' | 'new',
		contactNames?: string[]
	) => Promise<SyncToCrmResult | void> | void;
	/**
	 * Fired with the runId returned by POST /enrich. Parent navigates to the
	 * run detail view so the rep watches the same pipeline progress UI every
	 * other agent run uses.
	 */
	onEnrichStarted?: (runId: number) => void;
	/**
	 * Fired with the planId returned by POST /engagementPlan plus the trigger
	 * context (clientId/prospectingId/candidateId), so the parent can re-run
	 * generation on a failed plan (WEB-7934 retry). Parent navigates to the
	 * engagement plan review. Only offered for a company already in CRM that
	 * came from a run (needs runId + the CRM clientId).
	 */
	onPlanStarted?: (planId: number, ctx: { clientId: number; prospectingId: string; candidateId?: number }) => void;
	/**
	 * Changes when the run feeding this company progresses (the V2 slide-over
	 * stays open across an enrich run). Both fetches re-run on a change; the
	 * current content stays up while the fresh data loads.
	 */
	refreshToken?: string;
}) => {
	const dialProps = useDialProps();
	const [syncing, setSyncing] = useState(false);
	// What the last "Add contacts to Upsales" press did, so the button reports
	// its effect instead of only stopping the spinner; 'failed' when it threw.
	const [syncOutcome, setSyncOutcome] = useState<SyncToCrmResult | 'failed' | null>(null);
	const [dossier, setDossier] = useState<Dossier | null>(null);
	const [loading, setLoading] = useState(true);
	// Filled when we open the dossier for a sibling from Corporate Groups —
	// the row has no run-derived signals/contacts so we fetch whatever the
	// prospecting-api has cached for this prospectingId. Stays empty for the
	// ranked-candidate path, where `candidate.newsSignals` / `candidate.contacts`
	// are populated by the caller.
	const [cachedSignals, setCachedSignals] = useState<any[] | null>(null);
	const [cachedContacts, setCachedContacts] = useState<any[] | null>(null);
	// The V2 search row knows nothing about the CRM - the index does not - so a
	// caller that leaves `inCRM` undefined gets the answer from the same cache.
	const [cachedCrm, setCachedCrm] = useState<{ inCRM?: boolean; crmClient?: Candidate['crmClient'] } | null>(null);
	// Whether the dossier is already in the store, answered by /enrichments long
	// before a dossier that has to be written arrives - so the shimmer can say
	// which of the two it is waiting for. null until /enrichments answers.
	const [hasDossier, setHasDossier] = useState<boolean | null>(null);
	// Decision-makers the rep unticked (WEB-12857). Everyone starts ticked, so
	// adding without touching a box does what it always did.
	const [unpicked, setUnpicked] = useState<Set<string>>(new Set());

	// Browser-side half of the measurement (WEB-11036): the register facts
	// render from the row in the same commit as the open, contacts and signals
	// land with /enrichments, the dossier text last. Each is logged as ms since
	// the fetches started, next to the server's own per-hop timings.
	const openedAt = useRef(0);
	const mark = (what: string, detail?: unknown) => {
		const ms = Math.round(performance.now() - openedAt.current);
		try {
			performance.mark(`pa-dossier-${what}`);
		} catch {
			// No User Timing API: the console line below still carries the number.
		}
		console.info(`[PA] dossier ${what} after ${ms}ms`, candidate.prospectingId, detail ?? '');
	};

	// Which company the shimmer was last shown for: a refreshToken refetch keeps
	// the current dossier on screen instead of blanking it.
	const shimmeredFor = useRef<string | null>(null);
	useEffect(() => {
		if (!candidate.prospectingId) return;
		openedAt.current = performance.now();
		if (shimmeredFor.current !== candidate.prospectingId) {
			shimmeredFor.current = candidate.prospectingId;
			setLoading(true);
			setSyncOutcome(null);
			// The sections below no longer wait for the dossier, so the previous
			// company's cached answers must go before the new one's facts show.
			setDossier(null);
			setHasDossier(null);
			setCachedSignals(null);
			setCachedContacts(null);
			setCachedCrm(null);
			setUnpicked(new Set());
			mark('facts');
		}
		fetch(`/api/v2/prospectingAgentRuns/dossier/${encodeURIComponent(candidate.prospectingId)}`)
			.then(r => r.json())
			.then(data => {
				mark('dossier', data.timings);
				setDossier(data.error ? null : data);
			})
			.catch(() => {})
			.finally(() => setLoading(false));
	}, [candidate.prospectingId, refreshToken]);

	// When the caller didn't pre-populate signals/contacts (Corporate Groups
	// sibling click), pull from the per-prospectingId enrichment cache so any
	// previously-run dossier surfaces here too.
	useEffect(() => {
		if (!candidate.prospectingId) return;
		const hasSignals = (candidate.newsSignals ?? []).length > 0;
		const hasContacts = (candidate.contacts ?? []).length > 0;
		const knowsCrm = candidate.inCRM !== undefined;
		if (hasSignals && hasContacts && knowsCrm) return;
		fetch(`/api/v2/prospectingAgentRuns/enrichments/${encodeURIComponent(candidate.prospectingId)}`)
			.then(r => r.json())
			.then(data => {
				mark('enrichments');
				if (!hasSignals && Array.isArray(data.signals)) setCachedSignals(data.signals);
				if (!hasContacts && Array.isArray(data.contacts)) setCachedContacts(data.contacts);
				if (!knowsCrm && data.crm) setCachedCrm(data.crm);
				if (typeof data.hasDossier === 'boolean') setHasDossier(data.hasDossier);
			})
			.catch(() => {});
	}, [candidate.prospectingId, candidate.newsSignals, candidate.contacts, candidate.inCRM, refreshToken]);

	const isInCRM = candidate.inCRM ?? cachedCrm?.inCRM ?? false;
	const crmClient = candidate.crmClient ?? cachedCrm?.crmClient;
	const contacts = (candidate.contacts ?? []).length > 0 ? candidate.contacts ?? [] : cachedContacts ?? [];
	const signals = (candidate.newsSignals ?? []).length > 0 ? candidate.newsSignals ?? [] : cachedSignals ?? [];
	// Distinct connectors that actually contributed to this dossier — contact
	// discovery sources, the per-channel enrichment connectors (lusha/apollo/
	// hunter), plus the company register whenever firmographics are shown.
	// Drives the footer instead of a hardcoded label list.
	const contactSources = (() => {
		const set = new Set<string>();
		if (candidate.revenue || candidate.employees) set.add('creditsafe');
		for (const c of contacts as any[]) {
			if (c.source) set.add(c.source);
			const email = channelSource(c.contact?.emailSource, c.source);
			const phone = channelSource(c.contact?.phoneSource, c.source);
			const linkedin = channelSource(c.contact?.linkedinSource, c.source);
			[email, phone, linkedin].forEach(s => s && set.add(s));
		}
		return Array.from(set);
	})();
	// No chip at all without a score: "ICP 0/100" would read as a verdict on a
	// company nothing has judged yet.
	const scoreNum = typeof candidate.totalScore === 'number' ? Math.round(candidate.totalScore * 100) : null;
	const scoreCls =
		scoreNum === null ? '' : scoreNum >= 75 ? 'dx-score--high' : scoreNum >= 55 ? 'dx-score--med' : 'dx-score--low';
	const logoLetter = (candidate.name || 'C').charAt(0).toUpperCase();
	// Contacts can be picked wherever the header offers to write them.
	const canPick = !isInCRM || (!!onSync && !!crmClient?.id);
	const picked = (contacts as any[]).filter(c => !unpicked.has(c.name));
	// Only a narrowed pick is sent, so an untouched dossier imports as before.
	const pickedNames = unpicked.size > 0 ? picked.map(c => c.name) : undefined;
	const togglePicked = (name: string) =>
		setUnpicked(prev => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});

	return (
		<div className="dx">
			<button type="button" className="dx-back" onClick={onBack}>
				<ChevronLeft style={{ width: 16, height: 16 }} />
				{T('prospectingAgent.backToCandidates') || 'Back to candidates'}
			</button>

			{/* Premium header */}
			<div className="dx-header">
				<div className="dx-logo">{logoLetter}</div>
				<div className="dx-meta">
					<h2 className="dx-name">
						{candidate.name || candidate.prospectingId}
						{scoreNum !== null ? (
							<span className={`dx-score-chip ${scoreCls}`}>
								<Target style={{ width: 11, height: 11 }} />
								{`ICP ${scoreNum}/100`}
							</span>
						) : null}
						{candidate.domain ? (
							<a href={`https://${candidate.domain}`} target="_blank" rel="noopener noreferrer">
								<ExternalLink style={{ width: 11, height: 11 }} />
								{candidate.domain}
							</a>
						) : null}
					</h2>
					<div className="dx-pills">
						{candidate.city ? (
							<span className="dx-pill">
								<Building2 style={{ width: 11, height: 11 }} />
								{`${candidate.city}, ${candidate.countryCode || ''}`}
							</span>
						) : null}
						{candidate.employees ? (
							<span className="dx-pill">
								<Users style={{ width: 11, height: 11 }} />
								{`${candidate.employees} ${T('default.employees') || 'employees'}`}
							</span>
						) : null}
						{candidate.revenue ? (
							<span className="dx-pill">
								<BarChart3 style={{ width: 11, height: 11 }} />
								{`${formatRevenue(candidate.revenue)} SEK`}
							</span>
						) : null}
						{candidate.naceCode ? (
							<span className="dx-pill" title={`NACE ${candidate.naceCode}`}>
								<Hexagon style={{ width: 11, height: 11 }} />
								{T(`default.naceCode.${candidate.naceCode}`)}
							</span>
						) : null}
						{candidate.founded ? (
							<span className="dx-pill">
								<Calendar style={{ width: 11, height: 11 }} />
								{`${T('prospectingAgent.foundedShort') || 'Founded'} ${candidate.founded}`}
							</span>
						) : null}
					</div>
				</div>
				<div className="dx-actions">
					{/* Prospect & Enrich — kicks off a deep-research agent_run
					    against just this one prospectingId. The worker handles
					    contacts + people enrichment + dossier + signals + persist.
					    The run is observable on the run detail view + History,
					    survives page reloads, and writes back to the same per-pid
					    enrichment caches the dossier reads from on next open. */}
					{onEnrichStarted ? (
						/* eslint-disable-next-line @typescript-eslint/no-use-before-define */
						<EnrichButton
							prospectingId={candidate.prospectingId}
							hasData={signals.length > 0 || !!dossier?.summary}
							onStarted={onEnrichStarted}
						/>
					) : null}
					{isInCRM && crmClient?.id && candidate.runId && onPlanStarted ? (
						/* eslint-disable-next-line @typescript-eslint/no-use-before-define */
						<GenerateEngagementPlanButton
							runId={candidate.runId}
							clientId={crmClient.id}
							prospectingId={candidate.prospectingId}
							candidateId={candidate.id}
							onStarted={onPlanStarted}
						/>
					) : null}
					{isInCRM && onSync && crmClient?.id ? (
						/* eslint-disable-next-line @typescript-eslint/no-use-before-define */
						<SyncToCrmButton
							discovered={picked.length}
							syncing={syncing}
							outcome={syncOutcome}
							onClick={() => {
								setSyncing(true);
								setSyncOutcome(null);
								Promise.resolve(onSync(candidate.prospectingId, crmClient.id, 'sync', pickedNames))
									// A host that swallows its own errors answers void: no result line.
									.then(result => setSyncOutcome(result?.status === 'synced' ? result : null))
									.catch(() => setSyncOutcome('failed'))
									.finally(() => setSyncing(false));
							}}
						/>
					) : !isInCRM ? (
						<button
							type="button"
							className="dx-btn dx-btn--primary"
							onClick={() => onImport([candidate.prospectingId], pickedNames)}
						>
							<Plus style={{ width: 12, height: 12 }} />
							{T('prospectingAgent.addToCRM') || 'Add to CRM'}
						</button>
					) : null}
				</div>
			</div>

			{/* CRM banner */}
			{isInCRM && crmClient ? (
				<div className="dx-crm-banner">
					<div className="dx-crm-banner-icon">
						<Check style={{ width: 14, height: 14 }} />
					</div>
					<div className="dx-crm-banner-text">
						<strong>{T('prospectingAgent.crmAlreadyIn') || 'Already in your CRM'}</strong>
						{` · ${T('prospectingAgent.crmMatchedTo') || 'Matched to'} `}
						<em>{crmClient.name}</em>
						{` ${T('prospectingAgent.crmVia') || 'via'} `}
						{crmClient.matchReason === 'orgNo'
							? T('prospectingAgent.crmMatchOrgNumber') || 'org number'
							: crmClient.matchReason === 'name'
							? T('prospectingAgent.crmMatchName') || 'name'
							: T('prospectingAgent.crmMatchDomain') || 'domain'}
						<small>
							{T('prospectingAgent.crmAlreadyInDetail') ||
								'Existing activities, owners and opportunities will be preserved. New contacts can be merged.'}
						</small>
					</div>
					<a
						href={`/#/${AppService.getCustomerId()}/accounts/${crmClient.id}/`}
						className="dx-crm-banner-link"
					>
						{T('prospectingAgent.crmOpenIn') || 'Open in CRM →'}
					</a>
				</div>
			) : (
				<div className="dx-crm-banner dx-crm-banner--new">
					<div className="dx-crm-banner-icon">
						<Plus style={{ width: 14, height: 14 }} />
					</div>
					<div className="dx-crm-banner-text">
						<strong>{T('prospectingAgent.crmNotYet') || 'Not yet in your CRM'}</strong>
						{` · ${T('prospectingAgent.crmNoExistingRecord') || 'This company has no existing record'}`}
						<small>
							{T('prospectingAgent.crmAddingHint') ||
								'Adding will create a new client with discovered contacts, products and signals.'}
						</small>
					</div>
				</div>
			)}

			{/* Summary - the one section that waits for the dossier. The facts above
			    come from the row, the signals and contacts below from /enrichments,
			    so only this shimmers; it says "Writing" once /enrichments has told us
			    there is no stored dossier to load (WEB-11036). */}
			<div className="dx-section">
				<div className="dx-section-h">
					<span className="dx-section-label">{T('default.summary') || 'Summary'}</span>
					{!loading && dossier?.summary ? (
						<span className="dx-section-count">
							{(T('prospectingAgent.minRead') || '{n} min read').replace(
								'{n}',
								String(Math.ceil(dossier.summary.length / 200))
							)}
						</span>
					) : null}
				</div>
				{loading ? (
					<div className="dx-loading">
						{hasDossier === false
							? T('prospectingAgent.dossierWriting') || 'Writing the summary…'
							: T('prospectingAgent.dossierLoading') || 'Loading research dossier…'}
					</div>
				) : dossier?.summary ? (
					<p className="dx-prose">{dossier.summary}</p>
				) : (
					<p className="dx-prose dx-prose--empty">
						{T('prospectingAgent.dossierUnavailable') || 'No dossier available for this company.'}
					</p>
				)}
			</div>

			{/* Financials strip — only show if we have data */}
			{candidate.revenue ||
			candidate.employees ||
			dossier?.financials?.growth ||
			dossier?.financials?.profitMargin ? (
				<div className="dx-section">
					<div className="dx-section-h">
						<span className="dx-section-label">{T('prospectingAgent.financials') || 'Financials'}</span>
						<span className="dx-section-count">
							{(T('prospectingAgent.financialsSource') || 'Source: {source} · last 12 months').replace(
								'{source}',
								sourceLabel('creditsafe')
							)}
						</span>
					</div>
					<div className="dx-fin-strip">
						{candidate.revenue ? (
							<div className="dx-fin">
								<div className="dx-fin-v">{`${formatRevenue(candidate.revenue)} SEK`}</div>
								<div className="dx-fin-l">{T('account.revenue') || 'Revenue'}</div>
							</div>
						) : null}
						{dossier?.financials?.growth ? (
							<div className="dx-fin">
								<div className="dx-fin-v dx-fin-v--up">{dossier.financials.growth}</div>
								<div className="dx-fin-l">{T('prospectingAgent.yoyGrowth') || 'YoY growth'}</div>
							</div>
						) : null}
						{candidate.employees ? (
							<div className="dx-fin">
								<div className="dx-fin-v">{candidate.employees}</div>
								<div className="dx-fin-l">{T('default.employees') || 'Employees'}</div>
							</div>
						) : null}
						{dossier?.financials?.profitMargin ? (
							<div className="dx-fin">
								<div className="dx-fin-v">{dossier.financials.profitMargin}</div>
								<div className="dx-fin-l">{T('prospectingAgent.profitMargin') || 'Profit margin'}</div>
							</div>
						) : null}
					</div>
				</div>
			) : null}

			{/* Business signals */}
			<div className="dx-section">
				<div className="dx-section-h">
					<span className="dx-section-label">
						{T('prospectingAgent.businessSignals') || 'Business signals'}
					</span>
					<span className="dx-section-count">
						{signals.length > 0
							? (T('prospectingAgent.businessSignalsCount') || '{n} signals · last 6 months').replace(
									'{n}',
									String(signals.length)
							  )
							: T('prospectingAgent.businessSignalsLastSixMonths') || 'last 6 months'}
					</span>
				</div>
				{signals.length === 0 ? (
					<div className="dx-empty">
						{T('prospectingAgent.noRecentBusinessSignals') ||
							'No recent business signals for this company.'}
					</div>
				) : (
					<div className="dx-signals">
						{signals.map((s, i) => (
							<div key={i} className={`dx-signal dx-signal--${signalTone(s.type)}`}>
								<div className="dx-signal-icon">
									<SignalIcon type={s.type} />
								</div>
								<div className="dx-signal-body">
									<div className="dx-signal-head">
										<span className="dx-signal-type">{signalLabel(s.type)}</span>
										<span className="dx-signal-title">{s.title}</span>
									</div>
									{s.summary ? <p className="dx-signal-summary">{s.summary}</p> : null}
									<div className="dx-signal-foot">
										{s.confidence ? (
											<span className="dx-conf">
												<Check style={{ width: 10, height: 10 }} />
												{s.confidence}
											</span>
										) : null}
										{s.confidence && (s.source || s.sourceLabel) ? (
											<span className="dx-signal-sep">{' · '}</span>
										) : null}
										{s.source ? (
											<a href={s.source} target="_blank" rel="noopener noreferrer">
												{s.sourceLabel || T('prospectingAgent.signalSource') || 'source'}
											</a>
										) : s.sourceLabel ? (
											<span>{s.sourceLabel}</span>
										) : null}
									</div>
								</div>
								<span className="dx-signal-date">
									{s.date ? s.date : T('prospectingAgent.dateUnknown') || 'Date unknown'}
								</span>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Decision-makers */}
			<div className="dx-section">
				<div className="dx-section-h">
					<span className="dx-section-label">
						{T('prospectingAgent.decisionMakers') || 'Decision-makers'}
					</span>
					<span className="dx-section-count">
						{(T('prospectingAgent.decisionMakersDiscovered') || '{n} discovered').replace(
							'{n}',
							String(contacts.length)
						)}
						{contacts.filter((c: any) => c.contact?.email || c.email).length > 0
							? ` · ${(T('prospectingAgent.decisionMakersWithEmail') || '{n} with email').replace(
									'{n}',
									String(contacts.filter((c: any) => c.contact?.email || c.email).length)
							  )}`
							: ''}
					</span>
				</div>
				{contacts.length > 0 ? (
					<div className="dx-contacts">
						{contacts.map((c: any, i: number) => {
							const email = c.contact?.email || c.email;
							const linkedin = c.contact?.linkedin || c.linkedin;
							const phone = c.contact?.phone || c.phone;
							const initials = (c.name || '')
								.split(' ')
								.map((w: string) => w[0])
								.join('')
								.slice(0, 2)
								.toUpperCase();
							const isPrimary = i === 0;
							const source = c.source;
							const emailSrc = channelSource(c.contact?.emailSource, source);
							const phoneSrc = channelSource(c.contact?.phoneSource, source);
							const linkedinSrc = channelSource(c.contact?.linkedinSource, source);
							// `c.contact` is the CRM record when the hit is already saved, so the
							// call is logged against it; a hit that is not yet one dials with an
							// unknown party rather than refusing the number.
							const dial = c.contact?.id
								? dialProps(phone, { type: 'contact', record: c.contact })
								: dialProps(phone, { type: 'client', record: null });
							return (
								<div key={i} className={`dx-contact${isPrimary ? ' dx-contact--primary' : ''}`}>
									<div
										className="dx-contact-av"
										style={{ background: avatarColors[i % avatarColors.length] }}
									>
										{initials}
									</div>
									<div className="dx-contact-body">
										<div className="dx-contact-name">
											{c.name}
											{isPrimary ? (
												<span className="dx-contact-primary-tag">
													{T('prospectingAgent.primaryContact') || 'Primary'}
												</span>
											) : null}
										</div>
										<div className="dx-contact-role">
											{c.role || c.rawTitle || '—'}
											{source ? (
												<span className="dx-contact-source">
													{(T('prospectingAgent.foundVia') || 'found via {source}').replace(
														'{source}',
														sourceLabel(source)
													)}
												</span>
											) : null}
										</div>
										<div className="dx-contact-channels">
											{email ? (
												<a href={`mailto:${email}`} className="dx-channel-pill">
													<Mail style={{ width: 10, height: 10 }} />
													{T('default.email') || 'email'}
													{emailSrc ? (
														<span className="dx-channel-source">
															{sourceLabel(emailSrc)}
														</span>
													) : null}
												</a>
											) : null}
											{dial ? (
												<a
													href={dial.href}
													target={dial.target}
													onClick={dial.onClick}
													className="dx-channel-pill"
												>
													<Phone style={{ width: 10, height: 10 }} />
													{T('default.phone') || 'phone'}
													{phoneSrc ? (
														<span className="dx-channel-source">
															{sourceLabel(phoneSrc)}
														</span>
													) : null}
												</a>
											) : null}
											{linkedin ? (
												<a
													href={linkedin}
													target="_blank"
													rel="noopener noreferrer"
													className="dx-channel-pill"
												>
													<Link2 style={{ width: 10, height: 10 }} />
													{T('default.linkedin') || 'linkedin'}
													{linkedinSrc ? (
														<span className="dx-channel-source">
															{sourceLabel(linkedinSrc)}
														</span>
													) : null}
												</a>
											) : null}
										</div>
									</div>
									{canPick ? (
										<input
											type="checkbox"
											className="dx-contact-pick"
											checked={!unpicked.has(c.name)}
											onChange={() => togglePicked(c.name)}
											aria-label={T('prospectingAgent.includeContactInCrm', { name: c.name })}
											title={T('prospectingAgent.includeContactInCrm', { name: c.name })}
										/>
									) : null}
								</div>
							);
						})}
					</div>
				) : (
					<p className="dx-prose dx-prose--empty">
						{T('prospectingAgent.contactsNoneYet') || 'No contacts discovered yet.'}
					</p>
				)}
			</div>

			{/* Products + Customers */}
			{dossier?.products?.length || dossier?.customers?.length ? (
				<div className="dx-section dx-section--split">
					{dossier?.products && dossier.products.length > 0 ? (
						<div>
							<div className="dx-section-h" style={{ marginBottom: 10 }}>
								<span className="dx-section-label">
									{T('prospectingAgent.productsAndServices') || 'Products & services'}
								</span>
							</div>
							<ul className="dx-bullets" style={{ gap: 6 }}>
								{dossier.products.map((p, i) => (
									<li key={i} className="dx-bullet">
										<span className="dx-bullet-icon">
											<CheckSquare style={{ width: 13, height: 13 }} />
										</span>
										<span className="dx-bullet-body">
											<strong>{p.name}</strong>
											{p.description ? ` — ${p.description}` : ''}
										</span>
									</li>
								))}
							</ul>
						</div>
					) : null}
					{dossier?.customers && dossier.customers.length > 0 ? (
						<div>
							<div className="dx-section-h" style={{ marginBottom: 10 }}>
								<span className="dx-section-label">
									{T('prospectingAgent.knownCustomers') || 'Known customers'}
								</span>
								<span className="dx-section-count">{dossier.customers.length}</span>
							</div>
							<div className="dx-chip-list">
								{dossier.customers.map((c, i) => (
									<span key={i} className="dx-chip">
										{c.name}
									</span>
								))}
							</div>
						</div>
					) : null}
				</div>
			) : null}

			{/* Generated footer */}
			<div className="dx-meta-footer">
				{!loading ? (
					<span>
						<Sparkles style={{ width: 11, height: 11 }} />
						{` ${(T('prospectingAgent.dossierGenerated') || 'Generated {when}').replace(
							'{when}',
							dossier?.generatedAt ? new Date(dossier.generatedAt).toLocaleString() : '—'
						)}`}
					</span>
				) : null}
				<code>{candidate.prospectingId}</code>
				{contactSources.length > 0 ? (
					<span style={{ marginLeft: 'auto' }}>
						{(T('prospectingAgent.dossierSources') || 'Sources: {sources}').replace(
							'{sources}',
							contactSources.map(sourceLabel).join(' · ')
						)}
					</span>
				) : null}
			</div>
		</div>
	);
};

/**
 * The dossier's action for a company that is already in the CRM, named after
 * its effect: `mode: 'sync'` fills the client fields the register can and adds
 * the discovered contacts - never overwrites, never deletes (WEB-11035). The
 * hint says so before the press; afterwards it turns into what happened, and
 * the button stays disabled because a second press has nothing left to add.
 */
const SyncToCrmButton = ({
	discovered,
	syncing,
	outcome,
	onClick
}: {
	/** Contacts the dossier shows for this company. */
	discovered: number;
	syncing: boolean;
	outcome: SyncToCrmResult | 'failed' | null;
	onClick: () => void;
}) => {
	const synced = outcome && outcome !== 'failed' ? outcome : null;
	const added = synced?.contactsCreated ?? 0;
	const existed = synced?.contactsExisting ?? 0;
	const filled = synced?.fieldsUpdated?.length ?? 0;
	const nothingToAdd = !!synced && added === 0 && filled === 0 && (synced.contactsEnriched ?? 0) === 0;

	const label = syncing
		? T('prospectingAgent.addingToUpsales')
		: synced && !nothingToAdd
		? T(added > 0 ? 'prospectingAgent.contactsAdded' : 'prospectingAgent.companyUpdated')
		: discovered > 0
		? T('prospectingAgent.addContactsToUpsales', { count: discovered })
		: T('prospectingAgent.updateCompanyInUpsales');

	const hint =
		outcome === 'failed'
			? T('prospectingAgent.syncFailed')
			: nothingToAdd
			? T('prospectingAgent.syncNothingToAdd')
			: synced
			? [
					added > 0 ? T('prospectingAgent.syncResultContactsAdded', { count: added }) : null,
					existed > 0 ? T('prospectingAgent.syncResultContactsExisted', { count: existed }) : null,
					filled > 0 ? T('prospectingAgent.syncResultFieldsFilled', { count: filled }) : null
			  ]
					.filter(Boolean)
					.join(' · ')
			: discovered > 0
			? T('prospectingAgent.syncHintContacts', { count: discovered })
			: T('prospectingAgent.syncHintFields');

	return (
		<>
			<button type="button" className="dx-btn" disabled={syncing || !!synced} onClick={onClick}>
				{synced && !nothingToAdd ? (
					<Check style={{ width: 12, height: 12 }} />
				) : (
					<RefreshCw style={{ width: 12, height: 12 }} />
				)}
				{label}
			</button>
			<small className="dx-btn-hint">{hint}</small>
		</>
	);
};

/**
 * Inline "Prospect & Enrich" CTA shown in the dossier header. Posts to
 * `/api/v2/prospectingAgentRuns/enrich` to spawn a real agent_run for this
 * one prospectingId — same pipeline a normal run uses — and hands the rep
 * off to the run detail view via `onStarted` so they watch progress in the
 * same place every other run lives.
 *
 * Label flips between "Prospect & enrich" (no cached data) and "Re-enrich"
 * (when the dossier already shows something) so reps know whether they're
 * filling empty data or asking for fresh research.
 */
const GenerateEngagementPlanButton = ({
	runId,
	clientId,
	prospectingId,
	candidateId,
	onStarted
}: {
	runId: number;
	clientId: number;
	prospectingId: string;
	candidateId?: number;
	onStarted: (planId: number, ctx: { clientId: number; prospectingId: string; candidateId?: number }) => void;
}) => {
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	const run = async () => {
		if (busy) return;
		setBusy(true);
		setErr(null);
		try {
			const res = await fetch(`/api/v2/prospectingAgentRuns/${runId}/engagementPlan`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ clientId, prospectingId, candidateId })
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok || body?.error) {
				const msg =
					(typeof body?.error === 'string' && body.error) || body?.error?.message || `HTTP ${res.status}`;
				throw new Error(msg);
			}
			const data = body?.data ?? body;
			if (typeof data?.planId !== 'number') throw new Error('No planId returned by engagement plan endpoint');
			onStarted(data.planId, { clientId, prospectingId, candidateId });
		} catch (e) {
			setErr(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
			<button type="button" className="dx-btn" onClick={run} disabled={busy}>
				{busy ? (
					<Loader2 className="pa-spin" style={{ width: 12, height: 12 }} />
				) : (
					<CalendarClock style={{ width: 12, height: 12 }} />
				)}
				{busy
					? T('prospectingAgent.generatingPlan') || 'Generating…'
					: T('prospectingAgent.generatePlan') || 'Generate engagement plan'}
			</button>
			{err ? (
				<span className="pa-meta" style={{ color: 'var(--pa-danger, #b00020)', fontSize: 11 }}>
					{err}
				</span>
			) : null}
		</div>
	);
};

const EnrichButton = ({
	prospectingId,
	hasData,
	onStarted
}: {
	prospectingId: string;
	hasData: boolean;
	onStarted: (runId: number) => void;
}) => {
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	const run = async () => {
		if (busy) return;
		setBusy(true);
		setErr(null);
		try {
			const res = await fetch('/api/v2/prospectingAgentRuns/enrich', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ prospectingIds: [prospectingId] })
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok || body?.error) {
				const msg =
					(typeof body?.error === 'string' && body.error) || body?.error?.message || `HTTP ${res.status}`;
				throw new Error(msg);
			}
			const data = body?.data ?? body;
			if (typeof data?.runId !== 'number') throw new Error('No runId returned by enrich endpoint');
			onStarted(data.runId);
		} catch (e) {
			setErr(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
			<button type="button" className="dx-btn" onClick={run} disabled={busy}>
				{busy ? (
					<Loader2 className="pa-spin" style={{ width: 12, height: 12 }} />
				) : (
					<Sparkles style={{ width: 12, height: 12 }} />
				)}
				{busy
					? T('prospectingAgent.startingRun') || 'Starting run…'
					: hasData
					? T('prospectingAgent.reEnrich') || 'Re-enrich'
					: T('prospectingAgent.prospectEnrich') || 'Prospect & enrich'}
			</button>
			{err ? (
				<span className="pa-meta" style={{ color: 'var(--pa-danger, #b00020)', fontSize: 11 }}>
					{err}
				</span>
			) : null}
		</div>
	);
};

export default CompanyDossier;
